/** Static cold-country habitat. No snow depth, ice support or weather simulation.
 * A bounded cache of native terrain samples describes shelter at a fixed world
 * scale; trilinear interpolation is continuous across cube-face and LOD seams. */
import type {PlanetDef} from './universe.js';
import {hasLivingGround} from './habitat.js';
import * as pmath from './pmath.js';
import {rngFromString} from './rng.js';
const smooth=(a:number,b:number,x:number)=>{const t=Math.max(0,Math.min(1,(x-a)/(b-a)));return t*t*(3-2*t);};
const clamp=(x:number)=>Math.max(0,Math.min(1,x));
export const alpineEnabled=(def:PlanetDef)=>hasLivingGround(def)&&def.atmosphere!==null;
export const snowPotential=(temp:number,h=0,line=1e9)=>Math.max(1-smooth(-.65,-.35,temp),smooth(line,line+60,h));
/** The same formula is emitted in the terrain GLSL. Existing airless and ice
 * appearances use retention=-1 and keep their original snow rule. */
export function alpineSnow(temp:number,h:number,line:number,slope=1,retention=-1,patch=.5):number {
 const p=snowPotential(temp,h,line);
 const cover=retention<0?p:smooth(0,.1,p)*smooth(.18,.34,p+(retention-.5)*.5+(patch-.5)*.85);
 return cover*smooth(.72,.92,slope);
}
export function alpineTree(temp:number,h:number,line:number,retention=-1):number {
 if(retention<0)return 1;
 const stress=Math.max(1-smooth(-.72,-.28,temp),smooth(line-90,line+120,h));
 return 1-smooth(.18,.90,stress+( .5-retention)*.22);
}
export function alpineForest(temp:number,moist:number):number {
 // Preserve warm forest density; extend only the existing cold conifer band.
 const original=clamp((temp+.5)/.35),cold=smooth(-.74,-.15,temp);
 const thermal=original+(cold-original)*.5*(1-smooth(-.36,-.28,temp));
 return clamp((moist+.1)/.5)*thermal*clamp((.8-temp)/.35);
}
export class AlpineShelter {
 private readonly cache=new Map<string,number>();
 private cellX=Infinity;
 private cellY=Infinity;
 private cellZ=Infinity;
 private readonly corners=new Float64Array(8);
 private readonly wind:readonly number[];
 static readonly CELL_M=48;
 static readonly CACHE_LIMIT=16384;
 constructor(private readonly radius:number,seed:string,private readonly height:(x:number,y:number,z:number)=>number){
  const rng=rngFromString(`${seed}:alpine-prevailing-wind:1`);this.wind=[rng()*2-1,rng()*2-1,rng()*2-1];
 }
 get size():number{return this.cache.size;}
 private node(ix:number,iy:number,iz:number):number {
  const key=`${ix}:${iy}:${iz}`,old=this.cache.get(key);if(old!==undefined)return old;
  const v=[ix,iy,iz],l=pmath.hypot(v[0],v[1],v[2]),up=v.map(n=>n/l),axis=Math.abs(up[1])<.9?[0,1,0]:[1,0,0];
  const cross=(a:number[],b:readonly number[])=>[a[1]*b[2]-a[2]*b[1],a[2]*b[0]-a[0]*b[2],a[0]*b[1]-a[1]*b[0]];
  const ex=cross(up,axis),el=pmath.hypot(ex[0],ex[1],ex[2]);ex.forEach((_,i)=>ex[i]/=el);const ez=cross(up,ex),step=24;
  const sample=(a:number,b:number)=>{const d=up.map((n,i)=>n+(ex[i]*a+ez[i]*b)/this.radius),l=pmath.hypot(d[0],d[1],d[2]);return this.height(d[0]/l,d[1]/l,d[2]/l);};
  const center=sample(0,0),east=sample(step,0),west=sample(-step,0),north=sample(0,step),south=sample(0,-step),gx=(east-west)/(2*step),gz=(north-south)/(2*step);
  const curvature=((east+west+north+south)*.25-center)/6;
  const wx=ex.reduce((s,n,i)=>s+n*this.wind[i],0),wz=ez.reduce((s,n,i)=>s+n*this.wind[i],0),wl=pmath.hypot(wx,wz)||1;
  const windward=(gx*wx+gz*wz)/wl;
  const retention=clamp(.5+Math.max(-.32,Math.min(.32,curvature))-.18*pmath.hypot(gx,gz)-.22*windward);
  if(this.cache.size>=AlpineShelter.CACHE_LIMIT)this.cache.delete(this.cache.keys().next().value!);
  this.cache.set(key,retention);return retention;
 }
 sample(x:number,y:number,z:number):number {
  const px=x*this.radius/AlpineShelter.CELL_M,py=y*this.radius/AlpineShelter.CELL_M,pz=z*this.radius/AlpineShelter.CELL_M;
  const ix=Math.floor(px),iy=Math.floor(py),iz=Math.floor(pz),fx=px-ix,fy=py-iy,fz=pz-iz;
  // Adjacent mesh vertices usually share this 48 m cell. Reuse its corners
  // without eight string keys/Map lookups or temporary arrays per query.
  if(ix!==this.cellX||iy!==this.cellY||iz!==this.cellZ){
   this.cellX=ix;this.cellY=iy;this.cellZ=iz;
   for(let k=0;k<2;k++)for(let j=0;j<2;j++)for(let i=0;i<2;i++)this.corners[k*4+j*2+i]=this.node(ix+i,iy+j,iz+k);
  }
  let result=0;
  for(let k=0;k<2;k++)for(let j=0;j<2;j++)for(let i=0;i<2;i++)result+=this.corners[k*4+j*2+i]*(i?fx:1-fx)*(j?fy:1-fy)*(k?fz:1-fz);
  return result;
 }
}
